import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from "@/api/axios";
import { Home, Car, Smartphone, Fuel, TreePine, Droplets, Truck } from 'lucide-react';
import {useTranslation} from "react-i18next";

export default function EnvironmentalImpactCard({ selectedBins, selectedPeriod, selectedYear }) {
  const [currentItem, setCurrentItem] = useState(null);
  const [co2Data, setCo2Data] = useState({
    total_co2_without_garby: 0,
    total_co2_with_garby: 0,
    co2_savings: 0,
    equivalents: {},
    period_start: null,
    period_end: null
  });
  const [loading, setLoading] = useState(true);
  const {t} = useTranslation();

  const impactItems = [
    {
      icon: Home,
      color: 'text-emerald-500 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
      key: 'home_electricity',
    },
    {
      icon: Car,
      color: 'text-blue-500 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30',
      key: 'average_car_drive'
    },
    {
      icon: Smartphone,
      color: 'text-purple-500 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/30',
      key: 'smartphone_charges'
    },
    {
      icon: Fuel,
      color: 'text-yellow-500 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',
      key: 'gasoline_consumed'
    },
    {
      icon: TreePine,
      color: 'text-green-500 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/30',
      key: 'tree_sequestration'
    },
    {
      icon: Droplets,
      color: 'text-cyan-500 dark:text-cyan-400',
      bgColor: 'bg-cyan-50 dark:bg-cyan-900/30',
      key: 'water_bottles'
    },
    {
      icon: Truck,
      color: 'text-orange-500 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/30',
      key: 'lorry_drive'
    }
  ];

  const selectRandomEquivalent = useCallback((equivalents) => {
    const availableItems = impactItems.filter(item =>
      equivalents[item.key] && equivalents[item.key].value > 0
    );
    if (availableItems.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableItems.length);
      return availableItems[randomIndex];
    }
    return impactItems[0]; // fallback
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: selectedPeriod === 'year' ? 'numeric' : undefined
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchEnvironmentalImpact = async () => {
      try {
        setLoading(true);
        const params = {
          bins: selectedBins,
          period: selectedPeriod
        };
        if (selectedPeriod === 'year') {
          params.year = selectedYear;
        }

        const response = await axiosInstance.get('enviromental_impact', { params });

        if (isMounted) {
          setCo2Data(response.data);
          setCurrentItem(selectRandomEquivalent(response.data.equivalents));
        }
      } catch (error) {
        console.error('Error fetching environmental impact data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEnvironmentalImpact();

    return () => {
      isMounted = false;
    };
  }, [selectedBins, selectedPeriod, selectedYear]);

  const { co2_savings, equivalents, period_start, period_end } = co2Data;
  const currentValue = currentItem && equivalents[currentItem.key] ? equivalents[currentItem.key].value : 0;

  const getPeriodText = () => {
    if (!period_start || !period_end) return '';

    switch (selectedPeriod) {
      case 'day':
        return formatDate(period_start);
      case 'week':
      case '3months':
      case '6months':
        return `${formatDate(period_start)} - ${formatDate(period_end)}`;
      case 'month':
        return new Date(period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'year':
        return selectedYear.toString();
      default:
        return '';
    }
  };

  const getDisplayText = (item, value) => {
    const translationKey = `enviromentImpact.equivalents.${item.key}`;

    // Format the value based on the equivalent type
    let formattedValue;
    if (item.key === 'home_electricity') {
      formattedValue = value.toFixed(2);
    } else {
      formattedValue = value.toLocaleString();
    }

    return `${formattedValue} ${t(translationKey)}`;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium pb-2">{t('enviromentImpact.defaultTitle')}</h2>
      </div>

      <div className="px-4 pb-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
        {loading ? t('enviromentImpact.loading') : `${co2_savings.toFixed(2)} kg CO₂ ${t('enviromentImpact.co2SavedSuffix')}`}
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('enviromentImpact.fetchingData')}</p>
        ) : (
          currentItem && (
            <div className="w-full text-center">
              <div className={`w-12 h-12 rounded-full ${currentItem.bgColor} flex items-center justify-center mx-auto mb-2`}>
                <currentItem.icon className={`h-5 w-5 ${currentItem.color}`} />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {getDisplayText(currentItem, currentValue)}
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
}